import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { Alert, Box, Button, Divider, Typography } from '@mui/material';
import AppUserGenericDTO from '../../../DTOs/User/AppUserGenericDTO';
import DetailPanel from './DetailPanel';
import UserApiService from '../../../api/UserApiService';
import { formatPhoneNumber } from '../../../utilities/PhoneFormat';
import {
    getSmsConsentLabel,
    getSmsConsentSourceLabel,
    SmsConsentStatus,
} from '../../../utilities/SmsConsent';

export default function UserDetailPanel(props) {
    const { row, setSnackbarProperties, onConsentChanged } = props;

    const userApiService = new UserApiService();
    const [savingConsent, setSavingConsent] = useState(false);

    async function recordOptOut() {
        setSavingConsent(true);

        const response = await userApiService.updateSmsConsent(row.id, false);

        if (response?.success) {
            setSnackbarProperties(`${row.firstName} ${row.lastName} will no longer receive text messages`, 'success');
            onConsentChanged?.();
        } else {
            setSnackbarProperties(response?.message ?? 'Unable to update the text message preference', 'error');
        }

        setSavingConsent(false);
    }

    const address = [row.address1, row.address2, row.city, row.state, row.zip]
        .filter(Boolean)
        .join(', ');

    return (
        <DetailPanel
            sections={[
                {
                    title: 'Tenant',
                    fields: [
                        { label: 'Name', value: `${row.firstName} ${row.lastName}` },
                        { label: 'Email', value: row.username },
                        { label: 'Phone', value: formatPhoneNumber(row.phone) },
                        { label: 'Door code', value: row.doorCode ?? null },
                        { label: 'Licence', value: `${row.driversLicenseNumber} (${row.licenseState})` },
                        { label: 'Address', value: address || null },
                    ],
                },
                {
                    // The compliance record: what was agreed, when, and what agreed it.
                    title: 'Text message consent',
                    fields: [
                        { label: 'Status', value: getSmsConsentLabel(row.smsConsentStatus) },
                        {
                            label: 'Last changed',
                            value: row.smsConsentUpdatedUtc
                                ? new Date(row.smsConsentUpdatedUtc).toLocaleString()
                                : 'Never',
                        },
                        { label: 'Source', value: getSmsConsentSourceLabel(row.smsConsentSource) },
                    ],
                },
            ]}
        >
            <Divider sx={{ marginY: '16px' }} />

            {row.smsConsentStatus === SmsConsentStatus.OptedIn
                ? <Box>
                    <Button
                        variant={'outlined'}
                        color={'warning'}
                        disabled={savingConsent}
                        onClick={recordOptOut}
                    >
                        {savingConsent ? 'Saving...' : 'Record Opt-Out'}
                    </Button>
                    <Typography variant={'body2'} sx={{ color: 'text.secondary', marginTop: '8px' }}>
                        Use this when a tenant asks you to stop texting them, for instance over the
                        phone. It takes effect immediately, including for messages already queued.
                    </Typography>
                </Box>
                : <Alert severity={'info'}>
                    Only the tenant can opt themselves in, from their own account page or by texting
                    START. Consent recorded by staff on a tenant&apos;s behalf would not stand up if
                    they ever disputed being contacted.
                </Alert>
            }
        </DetailPanel>
    );
}

UserDetailPanel.propTypes = {
    row: PropTypes.objectOf(AppUserGenericDTO),
    setSnackbarProperties: PropTypes.func,
    onConsentChanged: PropTypes.func,
};
